import {
	VictoryAxis,
	VictoryChart,
	VictoryLabel,
	VictoryLine,
	VictoryTheme,
} from "victory-native";
import { useAppTheme } from "../../../useAppTheme";
import { CHART_DIMENSIONS } from "../constants";
import { useChartFontScale } from "../hooks/useChartFontScale";
import type { ComparisonChartData } from "../types";

type PerformanceComparisonChartProps = {
	data: ComparisonChartData;
	appFont?: string;
	containerWidth: number;
};

const CHART_ASPECT_RATIO = 0.85;

export const PerformanceComparisonChart = ({
	data,
	appFont,
	containerWidth,
}: PerformanceComparisonChartProps) => {
	const theme = useAppTheme();
	const config = CHART_DIMENSIONS.lineChart;
	const fontScale = useChartFontScale(containerWidth);
	const { series1, series2, xAxisLabel, yAxisLabel } = data;

	const chartWidth = containerWidth;
	const chartHeight = Math.round(containerWidth * CHART_ASPECT_RATIO);

	return (
		<VictoryChart
			theme={VictoryTheme.material}
			domain={{ y: [0, 100] }}
			padding={config.padding}
			height={chartHeight}
			width={chartWidth}
		>
			<VictoryAxis
				label={xAxisLabel}
				axisLabelComponent={
					<VictoryLabel
						dy={20}
						style={{
							fontSize: config.axisLabelFontSize * fontScale,
							fontFamily: appFont,
							fill: theme.colors.onSurfaceVariant,
						}}
					/>
				}
				style={{
					axis: { stroke: theme.colors.outlineVariant },
					tickLabels: {
						fontSize: config.tickLabelFontSize * fontScale,
						fontFamily: appFont,
						fill: theme.colors.onSurfaceVariant,
					},
					grid: { stroke: "none" },
				}}
			/>

			<VictoryAxis
				dependentAxis
				label={yAxisLabel}
				axisLabelComponent={
					<VictoryLabel
						dy={-35}
						style={{
							fontSize: config.axisLabelFontSize * fontScale,
							fontFamily: appFont,
							fill: theme.colors.onSurfaceVariant,
						}}
					/>
				}
				style={{
					axis: { stroke: theme.colors.outlineVariant },
					tickLabels: {
						fontSize: config.tickLabelFontSize * fontScale,
						fontFamily: appFont,
						fill: theme.colors.onSurfaceVariant,
					},
					grid: {
						stroke: theme.colors.surfaceVariant,
						strokeDasharray: "4,4",
					},
				}}
			/>

			<VictoryLine
				data={series1}
				style={{
					data: {
						stroke: theme.colors.onSurfaceVariant,
						strokeWidth: 2,
						strokeDasharray: "4,4",
					},
				}}
			/>

			<VictoryLine
				data={series2}
				style={{
					data: {
						stroke: theme.colors.primary,
						strokeWidth: 3,
					},
				}}
			/>
		</VictoryChart>
	);
};
